/**
 * fiko-govde-metadata.ts — Modelleme gövdesini (CSG mesh) ÖLÇÜLEBİLİR yapar.
 * STP import'un BREP analizinden ürettiği occt_faceGroups / occt_edgeGroups formatını
 * mesh'ten türetir → kumpas/cetvel/FikoGeometricSnap snap (vertex/edge/center/quadrant/radius)
 * modelleme gövdelerinde de FikoCizimYalin parçalar gibi çalışır. Yeni paralel sistem DEĞİL.
 */
import * as THREE from 'three';

export interface IFaceGrup {
  surfaceType: string; underlyingType: string;
  center?: { x: number; y: number; z: number };
  normal?: { x: number; y: number; z: number };
  axis?: { x: number; y: number; z: number };
  radius?: number;
}
export interface IEdgeGrup {
  curveType: string; edgeIndex: number;
  start?: { x: number; y: number; z: number };
  end?: { x: number; y: number; z: number };
  center?: { x: number; y: number; z: number };
  axis?: { x: number; y: number; z: number };
  radius?: number;
}

/** grup attribute (strt,syi,faceId) → her faceId için yüzey tipi + merkez/normal (+ silindir radius/axis). */
export function faceGruplariUret(meshGeo: THREE.BufferGeometry): IFaceGrup[] {
  const grup = meshGeo.attributes['grup'] as THREE.BufferAttribute | undefined;
  const pos = meshGeo.attributes['position'] as THREE.BufferAttribute | undefined;
  const nor = meshGeo.attributes['normal'] as THREE.BufferAttribute | undefined;
  if (!grup || !pos) return [];
  const fidMap = new Map<number, number[]>();
  let maxFid = 0;
  for (let i = 0; i < grup.count; i++) {
    const fid = grup.getZ(i); if (fid > maxFid) maxFid = fid;
    let a = fidMap.get(fid); if (!a) { a = []; fidMap.set(fid, a); } a.push(i);
  }
  const out: IFaceGrup[] = new Array(maxFid + 1);
  const c = new THREE.Vector3(), nAvg = new THREE.Vector3(), p = new THREE.Vector3(), nn = new THREE.Vector3();
  for (const [fid, verts] of fidMap) {
    c.set(0, 0, 0); nAvg.set(0, 0, 0);
    for (const v of verts) {
      p.fromBufferAttribute(pos, v); c.add(p);
      if (nor) { nn.fromBufferAttribute(nor, v); nAvg.add(nn); }
    }
    c.multiplyScalar(1 / verts.length);
    const nLen = nor ? nAvg.length() / verts.length : 1;     // 1≈düzlem (tutarlı), <1 eğri yüz
    nAvg.normalize();
    if (nLen >= 0.86) {
      out[fid] = { surfaceType: 'plane', underlyingType: 'plane', center: vec(c), normal: vec(nAvg) };
    } else {
      // Eğri yüz (silindir/küre): eksen ≈ ortalama normal dağılımının en zayıf yönü; radius = merkeze ort. uzaklık
      const eks = silindirEkseni(pos, verts);
      const r = ortalamaYaricap(pos, verts, c, eks);
      out[fid] = { surfaceType: 'cylinder', underlyingType: 'cylinder', center: vec(c), axis: vec(eks), radius: r, normal: vec(nAvg) };
    }
  }
  for (let i = 0; i <= maxFid; i++) if (!out[i]) out[i] = { surfaceType: 'plane', underlyingType: 'plane' };
  return out;
}

/**
 * kenarCikar() segment listesinden bağlı polyline'lar → LINE/CIRCLE grupları +
 * her ORİJİNAL segment'in ait olduğu edgeIndex (segEdgeId) → govdeKenar'a edgeId attribute
 * eklenince BREP-uyumlu kenar seçimi (bir çembere tık → tüm çember segment'leri birlikte).
 */
export function edgeGruplariUret(kenarGeo: THREE.BufferGeometry): { gruplar: IEdgeGrup[]; segEdgeId: Int32Array } {
  const pos = kenarGeo.attributes['position'] as THREE.BufferAttribute | undefined;
  const segSay = pos ? Math.floor(pos.count / 2) : 0;
  const segEdgeId = new Int32Array(segSay).fill(-1);
  if (!pos || pos.count < 2) return { gruplar: [], segEdgeId };
  const Q = 1e-3, key = (x: number, y: number, z: number) => `${Math.round(x / Q)},${Math.round(y / Q)},${Math.round(z / Q)}`;
  const segler: [number, number, number, number, number, number][] = [];
  for (let i = 0; i < pos.count; i += 2) {
    segler.push([pos.getX(i), pos.getY(i), pos.getZ(i), pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1)]);
  }
  const komsu = new Map<string, number[]>();
  segler.forEach((s, i) => {
    for (const k of [key(s[0], s[1], s[2]), key(s[3], s[4], s[5])]) { let a = komsu.get(k); if (!a) { a = []; komsu.set(k, a); } a.push(i); }
  });
  const kullanildi = new Array(segler.length).fill(false);
  const polyler: { nokta: THREE.Vector3[]; segIdx: number[] }[] = [];
  for (let i = 0; i < segler.length; i++) {
    if (kullanildi[i]) continue;
    const segIdx: number[] = [];
    const zincir = zincirle(i, segler, komsu, kullanildi, key, segIdx);
    if (zincir.length < 2) continue;
    let uzun = 0; for (let k = 1; k < zincir.length; k++) uzun += zincir[k].distanceTo(zincir[k - 1]);
    if (uzun > 1e-4) polyler.push({ nokta: zincir, segIdx });
  }
  const gruplar: IEdgeGrup[] = [];
  for (const poly of polyler) {
    const g = siniflandir(poly.nokta, gruplar.length);
    if (!g) continue;
    const ei = gruplar.length; gruplar.push(g);
    for (const si of poly.segIdx) if (si >= 0 && si < segSay) segEdgeId[si] = ei;
  }
  return { gruplar, segEdgeId };
}

// ---- yardımcılar ----
function vec(v: THREE.Vector3) { return { x: v.x, y: v.y, z: v.z }; }

function silindirEkseni(pos: THREE.BufferAttribute, verts: number[]): THREE.Vector3 {
  // kovaryans olmadan basit: en uzak iki vertex yönü ≈ eksen (yaklaşık)
  const a = new THREE.Vector3().fromBufferAttribute(pos, verts[0]);
  const b = new THREE.Vector3(); let enUzak = 0, eks = new THREE.Vector3(0, 0, 1);
  for (const v of verts) { b.fromBufferAttribute(pos, v); const d = b.distanceToSquared(a); if (d > enUzak) { enUzak = d; eks.subVectors(b, a); } }
  return eks.lengthSq() > 1e-9 ? eks.normalize() : new THREE.Vector3(0, 0, 1);
}
function ortalamaYaricap(pos: THREE.BufferAttribute, verts: number[], c: THREE.Vector3, eks: THREE.Vector3): number {
  const p = new THREE.Vector3(), d = new THREE.Vector3(); let toplam = 0;
  for (const v of verts) { p.fromBufferAttribute(pos, v); d.subVectors(p, c); d.addScaledVector(eks, -d.dot(eks)); toplam += d.length(); }
  return toplam / verts.length;
}
// Teğet zincir eşiği: ardışık segmentler bu açıdan fazla saparsa AYRI kenardır (keskin köşe).
// cos(25°)≈0.906 — kutu köşesi (90°, dot≈0) durur; yay segmentleri (küçük adım) zincirlenir.
const TEGET_COS = Math.cos(25 * Math.PI / 180);
function zincirle(bas: number, segler: any[], komsu: Map<string, number[]>, kullanildi: boolean[], key: (x: number, y: number, z: number) => string, segIdx?: number[]): THREE.Vector3[] {
  const s0 = segler[bas]; kullanildi[bas] = true; segIdx?.push(bas);
  const nokta: THREE.Vector3[] = [new THREE.Vector3(s0[0], s0[1], s0[2]), new THREE.Vector3(s0[3], s0[4], s0[5])];
  const _gel = new THREE.Vector3(), _git = new THREE.Vector3();
  // iki uçtan da büyüt — SADECE teğet (collinear) devam ederse (keskin köşede dur)
  for (let yon = 0; yon < 2; yon++) {
    let uc = yon === 0 ? nokta[nokta.length - 1] : nokta[0];
    let onceki = yon === 0 ? nokta[nokta.length - 2] : nokta[1];   // gelen yön referansı
    let devam = true;
    while (devam) {
      devam = false;
      _gel.subVectors(uc, onceki); const gl = _gel.length(); if (gl > 1e-9) _gel.multiplyScalar(1 / gl);
      const k = key(uc.x, uc.y, uc.z);
      for (const si of (komsu.get(k) || [])) {
        if (kullanildi[si]) continue;
        const s = segler[si];
        const p1 = new THREE.Vector3(s[0], s[1], s[2]), p2 = new THREE.Vector3(s[3], s[4], s[5]);
        const yeni = p1.distanceTo(uc) < 1e-3 ? p2 : p1;
        _git.subVectors(yeni, uc); const gtl = _git.length(); if (gtl > 1e-9) _git.multiplyScalar(1 / gtl);
        if (gl > 1e-9 && _gel.dot(_git) < TEGET_COS) continue;   // keskin köşe → bu segment AYRI kenar
        kullanildi[si] = true; segIdx?.push(si);
        if (yon === 0) nokta.push(yeni); else nokta.unshift(yeni);
        onceki = uc; uc = yeni; devam = true; break;
      }
    }
  }
  return nokta;
}
function siniflandir(pl: THREE.Vector3[], edgeIndex: number): IEdgeGrup | null {
  const bas = pl[0], son = pl[pl.length - 1];
  const kapali = bas.distanceTo(son) < 1e-4;   // kapalı döngü (çember adayı)
  // düz mü: tüm ara noktalar baş-son doğrusuna yakın (kapalı değilse)
  const dir = new THREE.Vector3().subVectors(son, bas);
  const uzun = dir.length();
  if (!kapali && uzun > 1e-4) {
    dir.multiplyScalar(1 / uzun);
    let maxSapma = 0; const tmp = new THREE.Vector3(), proj = new THREE.Vector3();
    for (const p of pl) { tmp.subVectors(p, bas); proj.copy(dir).multiplyScalar(tmp.dot(dir)); maxSapma = Math.max(maxSapma, tmp.distanceTo(proj)); }
    if (maxSapma < Math.max(0.02, uzun * 0.01)) {
      return { curveType: 'LINE', edgeIndex, start: vec(bas), end: vec(son) };
    }
  }
  // çember fit (3 ayrık nokta: baş, orta, son)
  const orta = pl[Math.floor(pl.length / 2)];
  const cember = cemberFit(bas, orta, kapali ? pl[Math.floor(pl.length / 3)] : son);
  if (cember) return { curveType: 'CIRCLE', edgeIndex, center: vec(cember.merkez), radius: cember.r, axis: vec(cember.eksen) };
  if (!kapali) return { curveType: 'LINE', edgeIndex, start: vec(bas), end: vec(son) };
  return null;   // dejenere kapalı (çember fit edilemedi) → atla
}
function cemberFit(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): { merkez: THREE.Vector3; r: number; eksen: THREE.Vector3 } | null {
  const ab = new THREE.Vector3().subVectors(b, a), ac = new THREE.Vector3().subVectors(c, a);
  const eksen = new THREE.Vector3().crossVectors(ab, ac);
  const l2 = eksen.lengthSq(); if (l2 < 1e-12) return null;
  eksen.normalize();
  // düzlemde çember merkezi (perpendicular bisector kesişimi, 3B)
  const ab2 = ab.lengthSq(), ac2 = ac.lengthSq();
  const cross = new THREE.Vector3().crossVectors(ab, ac);
  const denom = 2 * cross.lengthSq();
  if (denom < 1e-12) return null;
  const t1 = ac.clone().multiplyScalar(ab2).sub(ab.clone().multiplyScalar(ac2));
  const merkez = a.clone().add(new THREE.Vector3().crossVectors(t1, cross).divideScalar(denom));
  const r = merkez.distanceTo(a);
  if (!isFinite(r) || r <= 0 || r > 1e6) return null;
  return { merkez, r, eksen };
}
